import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import { useAtom, useAtomValue } from "jotai";
import { View } from "datasheets/model/core/Workbook";
import { Box, Pagination, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { AutoformViewConfig, viewType } from "./AutoformViewConfig";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import {
  datasetsAtom,
  viewAtom,
} from "datasheets/model/atoms/WorkbookFocusAtoms";
import { ViewReplicator } from "datasheets/model/logic/replicate";
import { PersistentKeyMapper } from "datasheets/model/utils/dictionaryUtils";
import { ViewProps, ViewFactory } from "datasheets/model/ViewFactory";
import { getOrCreateOrder } from "datasheets/model/logic/insertDeleteRows";

export function Autoform({ className }: { className?: string }) {
  const [view, setView] = useAtom(viewAtom);
  const viewConfig = view.config as AutoformViewConfig;
  const viewComponent =
    !!viewConfig.edit || !viewConfig.datasetId ? (
      <AutoformConfiguration setView={setView} />
    ) : (
      <AutoformRecordEntry datasetId={viewConfig.datasetId} />
    );
  return <div className={className}>{viewComponent}</div>;
}

function AutoformRecordEntry({ datasetId }: { datasetId: string }) {
  const datasets = useAtomValue(datasetsAtom);
  const dataset = datasets[datasetId];
  const cache = useAtomValue(dataCacheAtom);
  const data = cache[datasetId] ?? {};
  const [rowId, setRowId] = useState(0);
  const rowIdToObjectKey = (i: number) => getOrCreateOrder(dataset)[i];
  const row = data[rowIdToObjectKey(rowId)] ?? {}; // Should we skip empty rows?
  const pages = Math.max(...Object.keys(data).map(Number)) + 1;
  const properties = Object.entries(dataset.properties.nameToKey).map(
    ([name, key]) => {
      const value = row[key] ?? "";
      return <TextField key={name + value} label={name} value={value} />;
    }
  );
  const changePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setRowId(value - 1);
  };

  return (
    <>
      <Box height="2vh" />
      <Stack spacing={2}>{properties}</Stack>
      <Pagination count={pages} onChange={changePage} />
    </>
  );
}

function AutoformConfiguration({
  setView,
}: {
  setView: (update: View | ((arg: View) => View)) => void;
}) {
  function selectDatasetId(datasetId: string) {
    setView((view: View) => {
      return {
        ...view,
        ...{
          config: {
            ...view.config,
            ...{ edit: false, datasetId: datasetId },
          },
        },
      };
    });
  }
  return <DataSetSelector onSelect={selectDatasetId} selection={undefined} />;
}

function generate(): AutoformViewConfig {
  return {
    type: viewType,
    edit: true,
  };
}
function component(props: ViewProps) {
  return <Autoform className={props.className} />;
}
export default {
  register: function register() {
    ViewFactory.addView(viewType, generate, component);
    ViewReplicator.addReplicator(
      viewType,
      (
        config: AutoformViewConfig,
        propertyRemap: PersistentKeyMapper
      ): AutoformViewConfig => {
        return {
          ...config,
          ...{ datasetId: config.datasetId }, //Don't remap, as not owned
        };
      }
    );
  },
};
